import { useTranslation } from "react-i18next";

import { Icon } from "./Icon";

/**
 * Оценка звёздами: показ и ввод одним компонентом (доработки §6.1).
 *
 * Два режима, а не два компонента: разметка у них общая до последней звезды, и
 * разошедшись, они начали бы отличаться размером и зазором — на одном экране,
 * в паре сантиметров друг от друга.
 *
 * Без `onChange` это картинка: звёзды становятся `aria-hidden`, а смысл несёт
 * подпись рядом. Со `onChange` — группа радиокнопок: клавиатура и скринридер
 * обязаны уметь выбрать оценку, а не только палец.
 *
 * Оценка необязательна (§6.1), поэтому в режиме ввода повторное нажатие на
 * выбранную звезду сбрасывает выбор: иначе поставленную по ошибке оценку
 * снять нечем, и человеку остаётся отменять весь отзыв.
 */
const STARS = [1, 2, 3, 4, 5] as const;

interface StarRatingProps {
  value: number | null;
  /** Есть — компонент интерактивный. Нет — только показ. */
  onChange?: (value: number | null) => void;
  size?: number;
}

export function StarRating({ value, onChange, size = 16 }: StarRatingProps) {
  const { t } = useTranslation();

  if (!onChange) {
    return (
      <span
        className="inline-flex items-center gap-0.5"
        aria-label={value === null ? undefined : t("robots.comments.ratingValue", { value })}
      >
        {STARS.map((star) => (
          <Star key={star} filled={value !== null && star <= value} size={size} />
        ))}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1" role="radiogroup">
      {STARS.map((star) => (
        <button
          key={star}
          type="button"
          role="radio"
          aria-checked={value === star}
          aria-label={t("robots.comments.ratingValue", { value: star })}
          data-haptic="select"
          onClick={() => {
            onChange(value === star ? null : star);
          }}
          className="press rounded-full p-1"
        >
          <Star filled={value !== null && star <= value} size={size} />
        </button>
      ))}
    </span>
  );
}

function Star({ filled, size }: { filled: boolean; size: number }) {
  return (
    <span className={filled ? "text-status-profit" : "text-text-tertiary"}>
      <Icon name="star" size={size} />
    </span>
  );
}
